import { useState, useEffect } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  TextField, Button, Grid, Typography, Autocomplete, Chip,
  FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
  Box, CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import CustomerPreferences from '../../models/customer_preferences';

const FAMILY_COMPOSITION_OPTIONS = [
  'Trẻ em đang tuổi ăn dặm/nhỏ tuổi (Cần rau mềm, củ quả ngọt)',
  'Người lớn tuổi (Cần rau dễ tiêu hóa, tốt cho sức khỏe)',
  'Người trẻ/Trung niên (Cần rau đa dạng, Eat Clean, Salad)',
];

const COOKING_STYLE_OPTIONS = [
  'Ưu tiên rau/củ nấu canh/luộc truyền thống',
  'Ưu tiên củ quả xào/kho/hầm',
  'Ưu tiên các loại rau làm Salad/Eat Clean/Nước ép',
  "Mây's tự phối hợp đa dạng giúp nhà mình",
];

const MEALS_PER_DAY_OPTIONS = [
  '1 bữa/ngày',
  '2-3 bữa/ngày',
  'Trên 3 bữa/ngày',
];

const CustomerPreferencesPanel = ({ userId, onPreferencesLoaded }) => {
  const [prefs, setPrefs] = useState({
    family_size: '',
    family_composition: [],
    delivery_days: '',
    excluded_vegetables: '',
    favorite_vegetables: '',
    cooking_style: [],
    meals_per_day: '',
    auto_menu_consent: true,
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (userId) {
      loadPreferences();
    }
  }, [userId]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const model = new CustomerPreferences();
      const data = await model.getPreferences(userId);
      if (data) {
        setPrefs({
          family_size: data.family_size || '',
          family_composition: data.family_composition || [],
          delivery_days: data.delivery_days || '',
          excluded_vegetables: data.excluded_vegetables || '',
          favorite_vegetables: data.favorite_vegetables || '',
          cooking_style: data.cooking_style || [],
          meals_per_day: data.meals_per_day || '',
          auto_menu_consent: data.auto_menu_consent ?? true,
          notes: data.notes || '',
        });
        setLastUpdated(data.updated_at || null);
        if (onPreferencesLoaded) onPreferencesLoaded(true);
      }
    } catch (err) {
      console.error('Error loading preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const model = new CustomerPreferences();
      const result = await model.savePreferences(userId, prefs);
      if (result.success) {
        toast.success(result.message);
        setLastUpdated(result.data?.updated_at || new Date().toISOString());
        if (onPreferencesLoaded) onPreferencesLoaded(true);
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error('Có lỗi xảy ra khi lưu sở thích');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setPrefs(prev => ({ ...prev, [field]: value }));
  };

  if (!userId) return null;

  return (
    <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Sở thích khách hàng</Typography>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Cập nhật: {new Date(lastUpdated).toLocaleDateString('vi-VN')}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Row 1: Family size + Meals per day */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Quy mô gia đình"
                value={prefs.family_size}
                onChange={(e) => handleChange('family_size', e.target.value)}
                placeholder="VD: 3-4 người, 2 người lớn - 1 trẻ ăn dặm"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Số bữa/ngày</InputLabel>
                <Select
                  value={prefs.meals_per_day}
                  label="Số bữa/ngày"
                  onChange={(e) => handleChange('meals_per_day', e.target.value)}
                >
                  <MenuItem value=""><em>Chưa chọn</em></MenuItem>
                  {MEALS_PER_DAY_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Row 2: Family composition */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={FAMILY_COMPOSITION_OPTIONS}
                value={prefs.family_composition}
                onChange={(_, value) => handleChange('family_composition', value)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option.length > 40 ? option.substring(0, 40) + '...' : option}
                      {...getTagProps({ index })}
                      title={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Thành viên gia đình"
                    size="small"
                    placeholder="Chọn hoặc nhập thêm..."
                  />
                )}
              />
            </Grid>

            {/* Row 3: Cooking style */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={COOKING_STYLE_OPTIONS}
                value={prefs.cooking_style}
                onChange={(_, value) => handleChange('cooking_style', value)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip size="small" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Phong cách nấu"
                    size="small"
                    placeholder="Chọn phong cách..."
                  />
                )}
              />
            </Grid>

            {/* Row 4: Excluded + Favorite */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Rau KHÔNG ăn"
                multiline rows={2}
                value={prefs.excluded_vegetables}
                onChange={(e) => handleChange('excluded_vegetables', e.target.value)}
                placeholder="VD: mướp đắng, cần tây, hành lá..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Rau yêu thích"
                multiline rows={2}
                value={prefs.favorite_vegetables}
                onChange={(e) => handleChange('favorite_vegetables', e.target.value)}
                placeholder="VD: rau muống, súp lơ, rau cải..."
              />
            </Grid>

            {/* Row 5: Consent + Notes */}
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={prefs.auto_menu_consent}
                    onChange={(e) => handleChange('auto_menu_consent', e.target.checked)}
                  />
                }
                label="Đồng ý Mây's tự lên menu"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth size="small"
                label="Ghi chú thêm"
                value={prefs.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="VD: Cần rau giàu sắt cho bé ăn dặm..."
              />
            </Grid>

            {/* Save button */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Đang lưu...' : 'Lưu sở thích'}
              </Button>
            </Grid>
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomerPreferencesPanel;
